import React, { useCallback, useEffect, useRef, useState } from "react";
import ErrorScreen from "../../error/ErrorScreen";
import Loader from "../../Loader";
import AlertComponent from "../../alert/AlertComponent";
import HeatMapGraph from "../../../views/project/train/graph/HeatMapGraph";
import BarNivo from "../../../views/project/train/graph/BarNivo";
import PieNivo from "../../../views/project/train/graph/PieNivo";
import LineNivo from "../../../views/project/train/graph/LineNivo";
import RadarChart from "../../../views/project/train/graph/RadarChart";
import RadialChart from "../../../views/project/train/graph/RadialChart";
import ScatterChart from "../../../views/project/train/graph/ScatterChart";
import { getCokkie } from "../../../views/login/Login";
import getDataFromApi from "../../getDataFromApi";
import "../../Loader.scss";

const PLOTS_PAGE_SIZE = 4;
const RESPONSE_META_KEYS = new Set([
  "page",
  "page_size",
  "total_plots",
  "total_pages",
  "status",
]);

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const getPlotPayload = (incoming) => {
  if (!isPlainObject(incoming)) return {};

  if (isPlainObject(incoming.plots)) return incoming.plots;
  if (isPlainObject(incoming.plots_data)) return incoming.plots_data;

  return Object.entries(incoming).reduce((acc, [key, value]) => {
    if (!RESPONSE_META_KEYS.has(key)) acc[key] = value;
    return acc;
  }, {});
};

const mergePlotPayload = (previous, incoming) => {
  if (!isPlainObject(incoming)) return previous || {};

  return Object.entries(incoming).reduce(
    (acc, [key, value]) => {
      const previousValue = acc[key];

      if (
        isPlainObject(previousValue) &&
        isPlainObject(value) &&
        !previousValue.type &&
        !value.type
      ) {
        acc[key] = { ...previousValue, ...value };
      } else {
        acc[key] = value;
      }

      return acc;
    },
    { ...(isPlainObject(previous) ? previous : {}) }
  );
};

const hasGroupedBarPlots = (plotValue) =>
  isPlainObject(plotValue) &&
  !plotValue.type &&
  Object.values(plotValue).some(
    (childPlot) =>
      isPlainObject(childPlot) &&
      childPlot.type?.match("bar") &&
      Array.isArray(childPlot.data)
  );

const FULL_WIDTH_PLOT_TYPES = new Set([
  "heatmap_nivo",
  "line_nivo",
  "radar",
  "radial",
  "scatter_nivo",
]);

const getPlotWidth = (plotValue) => {
  if (hasGroupedBarPlots(plotValue) || FULL_WIDTH_PLOT_TYPES.has(plotValue?.type)) {
    return "full";
  }

  if (plotValue?.type?.match("bar") || plotValue?.type?.match("pie")) {
    return "half";
  }

  return null;
};

const getFullWidthPlotKeys = (plotData) => {
  const fullWidthKeys = new Set();
  let pendingHalfWidthKey = null;

  Object.entries(plotData || {}).forEach(([key, plotValue]) => {
    const width = getPlotWidth(plotValue);

    if (!width) return;

    if (width === "full") {
      if (pendingHalfWidthKey) {
        fullWidthKeys.add(pendingHalfWidthKey);
        pendingHalfWidthKey = null;
      }
      fullWidthKeys.add(key);
      return;
    }

    if (pendingHalfWidthKey) {
      pendingHalfWidthKey = null;
    } else {
      pendingHalfWidthKey = key;
    }
  });

  if (pendingHalfWidthKey) fullWidthKeys.add(pendingHalfWidthKey);

  return fullWidthKeys;
};

const appendPaginationParams = (url, page, isStream) => {
  const separator = url.includes("?")
    ? url.endsWith("?") || url.endsWith("&")
      ? ""
      : "&"
    : "?";

  return `${url}${separator}page=${page}&page_size=${PLOTS_PAGE_SIZE}&is_stream=${isStream}`;
};

const isTerminalEvaluationStatus = (status) =>
  ["complete", "terminated", "paused"].includes(
    String(status || "").toLowerCase()
  );

const buildPaginationItems = (current, max) => {
  if (max <= 12) return Array.from({ length: max }, (_, index) => index + 1);

  const items = new Set();

  [1, 2, 3, 4].forEach((page) => items.add(page));
  [max - 1, max].forEach((page) => items.add(page));

  for (let page = current - 2; page <= current + 2; page += 1) {
    if (page >= 1 && page <= max) items.add(page);
  }

  const sorted = Array.from(items).sort((a, b) => a - b);
  const output = [];

  for (let index = 0; index < sorted.length; index += 1) {
    output.push(sorted[index]);
    if (index < sorted.length - 1 && sorted[index + 1] - sorted[index] > 1) {
      output.push("...");
    }
  }

  return output;
};

export default function Plots({ id, type, query, evaluationSelect, EvalUrl, hub, message, evaluation_id, modelName, task_type, live, cache, setCache }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = React.useState();
  const [alert] = React.useState();
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState("1");
  const [totalPages, setTotalPages] = useState(null);
  const [streamSession, setStreamSession] = useState(0);
  const receivedDataRef = React.useRef(false);
  const dataRef = React.useRef();
  const pageRef = useRef(1);
  const totalPagesRef = useRef(null);
  const workerRef = useRef(null);
  const requestAbortRef = useRef(null);
  const activeRequestIdRef = useRef(0);
  const isPageLoadingRef = useRef(false);
  const sourceKeyRef = useRef("");
  const sourceModeRef = useRef("stream");
  const cacheRef = useRef(cache);
  const setCacheRef = useRef(setCache);
  const previousLiveRef = useRef(Boolean(live));
  const fullWidthPlotKeys = getFullWidthPlotKeys(data);

  const getBaseUrl = useCallback(() => {
    let nextUrl;
    if (EvalUrl) {
      nextUrl = `${window["ENV"].API_URL}/${EvalUrl}`;
    } else {
      nextUrl = `${window["ENV"].API_URL}/${type}/detailed/${id}/plots`;
    }

    if (query) nextUrl += query;

    return nextUrl;
  }, [EvalUrl, id, query, type]);

  useEffect(() => {
    cacheRef.current = cache;
  }, [cache]);

  useEffect(() => {
    setCacheRef.current = setCache;
  }, [setCache]);

  useEffect(() => {
    pageRef.current = page;
    setPageInput(String(page));
  }, [page]);

  useEffect(() => {
    totalPagesRef.current = totalPages;
  }, [totalPages]);

  useEffect(() => {
    const wasLive = previousLiveRef.current;
    const isLive = Boolean(live);
    previousLiveRef.current = isLive;

    if (wasLive && !isLive) {
      workerRef.current?.terminate();
      workerRef.current = null;
      requestAbortRef.current?.abort();
      requestAbortRef.current = null;
      activeRequestIdRef.current += 1;
      isPageLoadingRef.current = false;
      return;
    }

    if (!wasLive && isLive) {
      sourceModeRef.current = "stream";
      workerRef.current?.terminate();
      workerRef.current = null;
      requestAbortRef.current?.abort();
      requestAbortRef.current = null;
      activeRequestIdRef.current += 1;
      isPageLoadingRef.current = false;
      setStreamSession((session) => session + 1);
    }
  }, [live]);

  const goToPage = useCallback((nextPage) => {
    const requestedPage = Number(nextPage);
    if (!Number.isFinite(requestedPage)) return;

    const maxPages = totalPagesRef.current;
    const clampedPage = Math.max(
      1,
      Math.min(Math.floor(requestedPage), maxPages || Math.floor(requestedPage))
    );

    if (clampedPage === pageRef.current) return;

    if (EvalUrl && isTerminalEvaluationStatus(message)) {
      sourceModeRef.current = "terminal";
    }

    workerRef.current?.terminate();
    workerRef.current = null;
    requestAbortRef.current?.abort();
    requestAbortRef.current = null;
    activeRequestIdRef.current += 1;
    isPageLoadingRef.current = false;
    receivedDataRef.current = false;
    dataRef.current = undefined;
    setData(undefined);
    setLoading(true);
    setPage(clampedPage);
  }, [EvalUrl, message]);

  React.useEffect(() => {
    const sourceKey = `${EvalUrl || ""}|${type || ""}|${id || ""}|${query || ""}|${evaluation_id || ""}`;
    const sourceChanged = sourceKeyRef.current !== sourceKey;

    if (sourceChanged) {
      sourceKeyRef.current = sourceKey;
      sourceModeRef.current =
        Boolean(EvalUrl) && isTerminalEvaluationStatus(message) && !live
          ? "terminal"
          : "stream";
      workerRef.current?.terminate();
      workerRef.current = null;
      requestAbortRef.current?.abort();
      requestAbortRef.current = null;
      activeRequestIdRef.current += 1;
      isPageLoadingRef.current = false;
      receivedDataRef.current = false;
      dataRef.current = undefined;
      setData(undefined);
      setTotalPages(null);
      totalPagesRef.current = null;
      setLoading(true);

      if (page !== 1) {
        setPage(1);
        return;
      }
    }

    const cachedPlots = cacheRef.current;
    const hasCachedPlots = cachedPlots && Object.keys(cachedPlots).length > 0;
    const useWorker = sourceModeRef.current !== "terminal";

    receivedDataRef.current = false;
    dataRef.current = undefined;

    if (page === 1 && hasCachedPlots && !useWorker) {
      dataRef.current = cachedPlots;
      setData(cachedPlots);
      setLoading(false);
    } else {
      setData(undefined);
      setLoading(true);
    }

    if (message === "Failed") {
      setLoading(false);
      isPageLoadingRef.current = false;
      return;
    }

    const url = appendPaginationParams(getBaseUrl(), page, useWorker);
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;
    isPageLoadingRef.current = true;

    if (!useWorker) {
      const controller = new AbortController();
      requestAbortRef.current = controller;

      getDataFromApi(url, "GET", undefined, undefined, controller.signal)
        .then((incoming) => {
          if (
            controller.signal.aborted ||
            activeRequestIdRef.current !== requestId
          ) {
            return;
          }

          const nextTotalPages = Number(incoming?.total_pages);
          if (Number.isFinite(nextTotalPages)) {
            totalPagesRef.current = nextTotalPages;
            setTotalPages(nextTotalPages);
          }

          const plotPayload = getPlotPayload(incoming);
          dataRef.current = plotPayload;
          receivedDataRef.current = Object.keys(plotPayload).length > 0;
          setData(plotPayload);

          if (page === 1) {
            setCacheRef.current?.(plotPayload);
          }

          setLoading(false);
          isPageLoadingRef.current = false;
        })
        .catch((error) => {
          if (
            controller.signal.aborted ||
            activeRequestIdRef.current !== requestId
          ) {
            return;
          }

          console.error("Plots API Error:", error);
          setLoading(false);
          isPageLoadingRef.current = false;
        });

      return () => {
        controller.abort();
        if (requestAbortRef.current === controller) {
          requestAbortRef.current = null;
        }
      };
    }

    const plotworker = new URL('../../../worker/plotsSSEWorker.js', import.meta.url);
    const token = getCokkie("jwtToken");
    const worker = new Worker(plotworker, { type: 'module' });
    workerRef.current = worker;

    worker.onmessage = (event) => {
      if (
        workerRef.current !== worker ||
        activeRequestIdRef.current !== requestId
      ) {
        return;
      }

      const {
        type: workerMessageType,
        data: incoming,
        message: workerMessage,
      } = event.data;

      if (workerMessageType === "message") {
        if (incoming?.status === "done") {
          worker.terminate();
          if (workerRef.current === worker) workerRef.current = null;

          if (!receivedDataRef.current && !dataRef.current) setData({});

          setLoading(false);
          isPageLoadingRef.current = false;
          return;
        }

        const nextTotalPages = Number(incoming?.total_pages);
        if (Number.isFinite(nextTotalPages)) {
          totalPagesRef.current = nextTotalPages;
          setTotalPages(nextTotalPages);
        }

        const plotPayload = getPlotPayload(incoming);

        if (Object.keys(plotPayload).length > 0) {
          const previousData = receivedDataRef.current ? dataRef.current : undefined;
          const mergedData = mergePlotPayload(previousData, plotPayload);

          receivedDataRef.current = true;
          dataRef.current = mergedData;
          setData(mergedData);
          setLoading(false);
        }
      } else if (workerMessageType === "error") {
        console.error("SSE Error:", workerMessage);
        worker.terminate();
        if (workerRef.current === worker) workerRef.current = null;
        setLoading(false);
        isPageLoadingRef.current = false;
      }
    };

    worker.postMessage({ url, token });

    return () => {
      worker.terminate();
      if (workerRef.current === worker) workerRef.current = null;
    };
    // Status changes alone must not restart this effect. This preserves the
    // active stream during Evaluating -> Complete/Terminated. A subsequent
    // pagination action promotes the source to the normal JSON flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluation_id, EvalUrl, getBaseUrl, id, page, query, streamSession, type]);

  const noPlots =
    !loading &&
    (!data || Object.keys(data).length === 0);

  const showTerminatedMessage =
    noPlots && ["Terminated", "Failed", "Converting"].includes(message);

  const maxPages = Number.isFinite(Number(totalPages))
    ? Math.max(1, Number(totalPages))
    : 0;

  const submitPageInput = useCallback(() => {
    const requestedPage = Number(pageInput);
    if (!Number.isFinite(requestedPage)) return;
    if (requestedPage < 1 || (maxPages && requestedPage > maxPages)) return;

    goToPage(requestedPage);
  }, [goToPage, maxPages, pageInput]);

  const paginationFooter = maxPages > 1 && !showTerminatedMessage && (
    <div
      className="prediction-pagination-footer plots-pagination-footer px-2 col-12"
    >
      <div className="prediction-pagination-controls plots-pagination-controls">
        <div className="pagination-pages d-flex justify-content-center align-items-center gap-2">
          {buildPaginationItems(page, maxPages).map((item, index) => {
            if (item === "...") {
              return <span key={`plot-dots-${index}`} className="px-1">...</span>;
            }

            const pageNumber = item;
            const isActive = pageNumber === page;

            return (
              <button
                key={`plot-page-${pageNumber}`}
                className={`pagination-btn text ${isActive ? "active" : ""}`}
                disabled={isActive}
                onClick={() => goToPage(pageNumber)}
              >
                {pageNumber}
              </button>
            );
          })}
        </div>

        <div className="pagination-go d-flex justify-content-end align-items-center gap-1">
          <input
            value={pageInput}
            onChange={(event) => setPageInput(event.target.value.replace(/[^\d]/g, ""))}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                submitPageInput();
              }
            }}
            className="page-input text"
            placeholder="Page"
            style={{ width: 25 }}
          />
          <button className="evaluation-results-link-btn" onClick={submitPageInput}>
            GO
          </button>
        </div>
      </div>
    </div>
  );



  React.useEffect(() => {
    if (!message) return;

    // ✅ Normalize message for display
    let displayMessage = "";

    if (message === "Converting") {
      displayMessage = "Model Conversion is in Progress";
    } else if (message === "Terminated") {
      displayMessage = "Evaluation Terminated";
    } else if (message === "Failed") {
      displayMessage = "Evaluation Failed";
    }

    const textElement = document.getElementById("animated-text2");
    console.log(textElement);

    if (textElement) {
      textElement.innerHTML = displayMessage
        .split("")
        .map((char, index) => {
          if (char === " ") {
            return `<span style="display:inline-block; width: 10px;"></span>`;
          }
          // ⚠️ you had `animation-delay2` typo → should be `animation-delay`
          return `<span style="animation-delay: ${index * 0.1}s">${char}</span>`;
        })
        .join("");
    }
  }, [message, showTerminatedMessage]);






  return (
    <div
      className="plots-shell d-flex flex-column ps-2 pt-2 w-100"
      style={{
        height: '100%',
        flex: hub ? "1 1 auto" : undefined,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <AlertComponent alert={alert} />
      <div
        className="plots-scroll-body card-padding-bottom w-100 flex-grow-1 overflow-auto pe-1"
        style={{ minHeight: 0, scrollbarWidth: "thin", alignContent: "flex-start" }}
      >
        {showTerminatedMessage ? (
          <div className="plots-full-row d-flex justify-content-center mt-2">
            <div className="p-2" style={{ maxWidth: "100%" }}>
              <p id="animated-text2" className="animated-text2 m-0 p-0"></p>
            </div>
          </div>
        ) : !loading ? (
          data && Object.keys(data).length ? (
            <>
              {Object.keys(data).map((val) =>
                data[val]?.type?.match("bar") ? (
                  <div key={val} className={`plots-chart-col ${fullWidthPlotKeys.has(val) ? "plots-chart-col--full" : "plots-chart-col--half"}`}>
                    <div className="grey-card-white-simple mb-4">
                      <BarNivo
                        key={val}
                        data={data[val].data}
                        title={val}
                        evaluationSelect={evaluationSelect}
                        xlabel={data[val].xlabel} ylabel={data[val].ylabel}
                        integerYAxisTicks={val === "Class Diversity Distribution"}
                        evaluation_id={evaluation_id}
                        modelName={modelName}
                        task_type={task_type}
                      />
                    </div>
                  </div>
                ) : hasGroupedBarPlots(data[val]) ? (
                  <div key={val} className="plots-chart-col plots-chart-col--full">
                    <div className="grey-card-white-simple mb-4">
                      <BarNivo
                        key={val}
                        title={val}
                        plotOptions={data[val]}
                        evaluationSelect={evaluationSelect}
                        evaluation_id={evaluation_id}
                        modelName={modelName}
                        task_type={task_type}
                      />
                    </div>
                  </div>
                ) :
                  data[val]?.type?.match("pie") ? (
                    <div className={`plots-chart-col ${fullWidthPlotKeys.has(val) ? "plots-chart-col--full" : "plots-chart-col--half"}`} key={val}>
                      <div className="grey-card-white-simple mb-4">
                        <PieNivo
                          key={val}
                          data={data[val].data}
                          title={val}
                          Colors={data.hexCodes}
                          unit={data[val]?.unit ?? data[val]?.yunit ?? data[val]?.y_unit}
                        ></PieNivo>
                      </div>
                    </div>
                  ) :
                    data[val]?.type === "heatmap_nivo" ? (
                      <div className="plots-chart-col plots-chart-col--full" key={val}>
                        <div className="grey-card-white-simple mb-4">
                          <HeatMapGraph
                            key={val}
                            data={data[val].data}
                            title={val}
                            xLabel={data[val]?.xlabel ?? data[val]?.x_label}
                            yLabel={data[val]?.ylabel ?? data[val]?.y_label}
                            evaluation_id={evaluation_id}
                            modelName={modelName}
                            task_type={task_type}
                          />
                        </div>
                      </div>
                    ) : data[val]?.type === "line_nivo" ? (
                      <div className="plots-chart-col plots-chart-col--full" key={val}>
                        <div className="grey-card-white-simple mb-4">
                          <LineNivo
                            key={val}
                            singleGraphData={[
                              {
                                id: val,
                                data: Array.isArray(data[val]?.data) ? data[val].data : [],
                              },
                            ]}
                            title={val}
                            xLabel={data[val]?.xlabel ?? data[val]?.x_label}
                            yLabel={data[val]?.ylabel ?? data[val]?.y_label}
                            xUnit={data[val]?.xunit ?? data[val]?.x_unit}
                            yUnit={data[val]?.yunit ?? data[val]?.y_unit}
                          />
                        </div>
                      </div>
                    )
                      : data[val]?.type === 'radar' ?
                        <div className="plots-chart-col plots-chart-col--full" key={val}>
                          <div className="grey-card-white-simple mb-4">
                            <h6 className="text-center p-3 fw-bold">{val}</h6>
                            <RadarChart data={data[val]?.data} />
                          </div>
                        </div>
                        : data[val]?.type === 'radial' ?
                          <div className="plots-chart-col plots-chart-col--full" key={val}>
                            <div className="grey-card-white-simple mb-4">
                              <h6 className="text-center p-3 fw-bold">{val}</h6>
                              <RadialChart data={data[val]?.data} title={val} />
                            </div>
                          </div>
                          : data[val]?.type === 'scatter_nivo' ?
                            <div className="plots-chart-col plots-chart-col--full" key={val}>
                              <div className="grey-card-white-simple mb-4">
                                <ScatterChart
                                  data={data[val]?.data}
                                  title={val}
                                  xlabel={data[val]?.xlabel ?? data?.plots_data?.[val]?.xlabel}
                                  ylabel={data[val]?.ylabel ?? data?.plots_data?.[val]?.ylabel}
                                  evaluation_id={evaluation_id}
                                  modelName={modelName}
                                  task_type={task_type}
                                />
                              </div>
                            </div>
                            : null
              )}
            </>
          ) : (
            <div className="plots-full-row">
              <ErrorScreen message={"No plots Found "} />
            </div>
          )
        ) : (
          (["Terminated", "Failed", "Converting"].includes(message) ?
            <div className="plots-full-row d-flex justify-content-center mt-2">
              <div className="p-2" style={{ maxWidth: "100%" }}>
                <div className="d-a gap-2">
                  <div>

                  </div> <p id="animated-text2" className="animated-text2 m-0 p-0"></p>
                </div>

              </div>
            </div>
            :
            <div className="plots-full-row plots-loader-cell">
              <Loader />
            </div>
          )
        )}
      </div>
      {paginationFooter}
    </div>
  );
}
